import React, { useMemo } from 'react';

import {
  Button,
  Divider,
  Grid,
  Group,
  LoadingOverlay,
  NumberInput,
  Paper,
  Select,
  Switch,
  Text,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, useShallowEffect } from '@mantine/hooks';
import MoneyField from 'components/MoneyField';
import useUpdateGym from 'hooks/gym/useUpdateGym';
import { useTracking } from 'hooks/mixpanel/useTracking';
import { GymRetrieve, GymWrite } from 'types';
import { TimeUnitEnum } from 'types/enums';
import { setMaxLateCancellationIntervalCount } from 'utils/functions';
import notificationHandler from 'utils/notifications';

interface FormValues {
  classReservationWindowInterval: 'minute' | 'hour' | 'day';
  classReservationWindowIntervalCount: number;
  classReservationWindowCloseInterval: 'minute' | 'hour' | 'day';
  classReservationWindowCloseIntervalCount: number;
  classAllowLateCancellation: boolean;
  classCancellationWindowInterval: 'minute' | 'hour' | 'day';
  classCancellationWindowIntervalCount: number;
  classUseCancellationFee: boolean;
  classUseNoShowFee: boolean;
  classCancellationFee: number;
  classCancellationCreditDeduction: boolean;
  classNoShowFee: number;
  classNoShowCreditDeduction: boolean;
  classWaitlistMax: number;
  classDisableAthleteReservationsWithPastDue: boolean;
  classLimitOneReservationPerDay: boolean;
  appointmentLimitOnePerDay: boolean;
  appointmentUseCancellationFee: boolean;
  appointmentUseNoShowFee: boolean;
  appointmentAllowLateCancellation: boolean;
  appointmentCancellationWindowInterval: 'minute' | 'hour' | 'day';
  appointmentCancellationWindowIntervalCount: number;
  appointmentCancellationFee: number;
  appointmentCancellationCreditDeduction: boolean;
  appointmentNoShowFee: number;
  appointmentNoShowCreditDeduction: boolean;
  lateCancelNoShowTaxRate: number;
}

const timeUnitOptions = [
  { value: TimeUnitEnum.MINUTE, label: `Min(s)` },
  { value: TimeUnitEnum.HOUR, label: `Hour(s)` },
  { value: TimeUnitEnum.DAY, label: `Day(s)` },
];

const ReservationWaitlistCancelationsCard = ({
  gymData,
  gymDataIsLoading,
  gymDataIsSuccess,
  permission = true,
}: {
  gymData?: GymRetrieve;
  gymDataIsLoading: boolean;
  gymDataIsSuccess: boolean;
  permission?: boolean;
}): JSX.Element => {
  const initialValues = useMemo((): FormValues => {
    const classesEventSettings = gymData?.eventSettings?.classes;
    const appointmentsEventSettings = gymData?.eventSettings?.appointments;

    return {
      classReservationWindowInterval: classesEventSettings?.reservationWindowOpenInterval ?? 'day',
      classReservationWindowIntervalCount: classesEventSettings?.reservationWindowOpenIntervalCount ?? 7,
      classReservationWindowCloseInterval: classesEventSettings?.reservationWindowCloseInterval ?? 'hour',
      classReservationWindowCloseIntervalCount:
        classesEventSettings?.reservationWindowCloseIntervalCount ?? 1,
      classAllowLateCancellation: classesEventSettings?.allowLateCancellation ?? true,
      classCancellationWindowInterval: classesEventSettings?.cancellationInterval ?? 'hour',
      classCancellationWindowIntervalCount: classesEventSettings?.cancellationIntervalCount ?? 0,
      classUseCancellationFee: classesEventSettings?.cancellationFee !== 0,
      classUseNoShowFee: classesEventSettings?.noShowFee !== 0,
      classCancellationFee: classesEventSettings?.cancellationFee ?? 0,
      classCancellationCreditDeduction: classesEventSettings?.cancellationCreditDeduction ?? false,
      classNoShowFee: classesEventSettings?.noShowFee ?? 0,
      classNoShowCreditDeduction: classesEventSettings?.noShowCreditDeduction ?? false,
      classWaitlistMax: classesEventSettings?.waitlistMax ?? 5,
      classDisableAthleteReservationsWithPastDue:
        classesEventSettings?.disableAthleteReservationsWithPastDue ?? false,
      classLimitOneReservationPerDay: classesEventSettings?.limitOneClassReservationPerDay ?? false,
      appointmentLimitOnePerDay: appointmentsEventSettings?.limitOneAppointmentPerDay ?? false,
      appointmentUseCancellationFee: appointmentsEventSettings?.cancellationFee !== 0,
      appointmentUseNoShowFee: appointmentsEventSettings?.noShowFee !== 0,
      appointmentAllowLateCancellation: appointmentsEventSettings?.allowLateCancellation ?? true,
      appointmentCancellationWindowInterval: appointmentsEventSettings?.cancellationInterval || 'hour',
      appointmentCancellationWindowIntervalCount: appointmentsEventSettings?.cancellationIntervalCount || 0,
      appointmentCancellationFee: appointmentsEventSettings?.cancellationFee || 0,
      appointmentCancellationCreditDeduction:
        appointmentsEventSettings?.cancellationCreditDeduction ?? false,
      appointmentNoShowFee: appointmentsEventSettings?.noShowFee ?? 0,
      appointmentNoShowCreditDeduction: appointmentsEventSettings?.noShowCreditDeduction ?? false,
      lateCancelNoShowTaxRate: parseFloat(gymData?.lateCancelNoShowTaxRate ?? '0') * 100,
    };
  }, [
    gymData?.eventSettings?.appointments,
    gymData?.eventSettings?.classes,
    gymData?.lateCancelNoShowTaxRate,
  ]);

  const form = useForm<FormValues>({
    initialValues,
  });

  const [isEditable, { open, close }] = useDisclosure(false);

  const updateMutation = useUpdateGym();
  const { trackReservationLimitSettingChanged } = useTracking();
  const handleSubmit = (formData: FormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();

    if (gymData) {
      const request: GymWrite = {
        ...gymData,
        gymMailConfig: null,
        eventSettings: {
          classes: {
            reservationWindowOpenInterval: formData.classReservationWindowInterval ?? 'day',
            reservationWindowOpenIntervalCount: formData.classReservationWindowIntervalCount ?? 7,
            reservationWindowCloseInterval: formData.classReservationWindowCloseInterval ?? 'hour',
            reservationWindowCloseIntervalCount: formData.classReservationWindowCloseIntervalCount ?? 1,
            allowLateCancellation: formData.classAllowLateCancellation ?? true,
            cancellationInterval: formData.classCancellationWindowInterval ?? 'hour',
            cancellationIntervalCount: formData.classCancellationWindowIntervalCount ?? 0,
            cancellationFee:
              formData.classUseCancellationFee && formData.classAllowLateCancellation
                ? formData.classCancellationFee
                : 0,
            cancellationCreditDeduction: formData.classAllowLateCancellation
              ? formData.classCancellationCreditDeduction
              : false,
            noShowFee: formData.classUseNoShowFee ? formData.classNoShowFee : 0,
            noShowCreditDeduction: formData.classNoShowCreditDeduction ?? false,
            waitlistMax: formData.classWaitlistMax ?? 5,
            disableAthleteReservationsWithPastDue:
              formData.classDisableAthleteReservationsWithPastDue || false,
            limitOneClassReservationPerDay: formData.classLimitOneReservationPerDay || false,
          },
          appointments: {
            limitOneAppointmentPerDay: formData.appointmentLimitOnePerDay || false,
            allowLateCancellation: formData.appointmentAllowLateCancellation ?? true,
            cancellationInterval: formData.appointmentCancellationWindowInterval ?? 'hours',
            cancellationIntervalCount: formData.appointmentCancellationWindowIntervalCount ?? 0,
            cancellationFee:
              formData.appointmentUseCancellationFee && formData.appointmentAllowLateCancellation
                ? formData.appointmentCancellationFee
                : 0,
            cancellationCreditDeduction: formData.appointmentAllowLateCancellation
              ? formData.appointmentCancellationCreditDeduction
              : false,
            noShowFee: formData.appointmentUseNoShowFee ? formData.appointmentNoShowFee : 0,
            noShowCreditDeduction: formData.appointmentNoShowCreditDeduction ?? false,
          },
        },
        lateCancelNoShowTaxRate: String(formData.lateCancelNoShowTaxRate / 100),
      };

      updateMutation.mutate(request, {
        onSuccess: response => {
          successNotification('Gym settings update success', `${response.data.title} successfully updated.`);
          const previousClassLimit =
            gymData?.eventSettings?.classes?.limitOneClassReservationPerDay ?? false;
          const previousAppointmentLimit =
            gymData?.eventSettings?.appointments?.limitOneAppointmentPerDay ?? false;
          if (formData.classLimitOneReservationPerDay !== previousClassLimit) {
            trackReservationLimitSettingChanged('classes', formData.classLimitOneReservationPerDay);
          }
          if (formData.appointmentLimitOnePerDay !== previousAppointmentLimit) {
            trackReservationLimitSettingChanged('appointments', formData.appointmentLimitOnePerDay);
          }
          close();
        },
        onError: error => {
          errorNotification('Gym settings update failed', error.response?.data.message);
        },
      });
    }
  };

  const handleCancelChanges = (): void => {
    form.setValues(initialValues);
    close();
  };

  useShallowEffect(() => {
    if (gymData && gymDataIsSuccess) {
      form.setValues(initialValues);
    }
  }, [
    gymData?.eventSettings?.classes,
    gymData?.eventSettings?.appointments,
    gymData?.lateCancelNoShowTaxRate,
  ]);

  return (
    <Paper shadow='xl' p='sm'>
      <LoadingOverlay visible={gymDataIsLoading} />

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Group position='apart' spacing='md' noWrap sx={{ alignItems: 'flex-start' }}>
          <Title order={4}>Reservation, Waitlist, and Cancellation Settings</Title>

          <Group spacing='xs' noWrap position='right'>
            {isEditable ? (
              <>
                <Button
                  color='red'
                  compact
                  onClick={handleCancelChanges}
                  disabled={updateMutation.isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  color='green'
                  compact
                  loading={updateMutation.isLoading}
                  data-cy='gymSettingsReservationSaveButton'
                >
                  Save
                </Button>
              </>
            ) : (
              <Button
                compact
                onClick={open}
                disabled={!permission}
                data-cy='gymSettingsReservationEditButton'
              >
                Edit
              </Button>
            )}
          </Group>
        </Group>

        <Divider mx='-sm' my='sm' />

        <Title order={4} px='xs'>
          Classes
        </Title>

        <Grid align='center' gutter='md' p='xs'>
          <Grid.Col span={3}>
            <NumberInput
              size='xs'
              disabled={!isEditable}
              min={0}
              {...form.getInputProps('classWaitlistMax')}
              required
              data-cy='gymSettingsReservationWaitlist'
            />
          </Grid.Col>
          <Grid.Col span={9}>
            <Text size='sm'>Maximum amount of athletes allowed in waitlist </Text>
          </Grid.Col>

          <Grid.Col span={3}>
            <Group noWrap grow spacing='xs'>
              <NumberInput
                size='xs'
                disabled={!isEditable}
                min={0}
                {...form.getInputProps('classReservationWindowIntervalCount')}
                required
                data-cy='gymSettingsReservationTimeAccepted'
              />
              <Select
                size='xs'
                data={timeUnitOptions}
                disabled={!isEditable}
                {...form.getInputProps('classReservationWindowInterval')}
                data-cy='gymSettingsReservationTimeAcceptedSelect'
              />
            </Group>
          </Grid.Col>
          <Grid.Col span={9}>
            <Text size='sm'>Time before a class when reservations will be accepted</Text>
          </Grid.Col>

          <Grid.Col span={3}>
            <Group noWrap grow spacing='xs'>
              <NumberInput
                size='xs'
                disabled={!isEditable}
                min={0}
                {...form.getInputProps('classReservationWindowCloseIntervalCount')}
                required
                data-cy='gymSettingsReservationTimeNotAccepted'
              />
              <Select
                size='xs'
                data={timeUnitOptions}
                disabled={!isEditable}
                {...form.getInputProps('classReservationWindowCloseInterval')}
                data-cy='gymSettingsReservationTimeNotAcceptedSelect'
              />
            </Group>
          </Grid.Col>
          <Grid.Col span={9}>
            <Text size='sm'>Time before a class when reservations will no longer be accepted</Text>
          </Grid.Col>

          <Grid.Col span={12}>
            <Switch
              disabled={!isEditable}
              label='Disable athlete reservations if they have a past due bill '
              checked={form.values.classDisableAthleteReservationsWithPastDue}
              data-cy='gymSettingsReservationDisablePastDue'
              {...form.getInputProps('classDisableAthleteReservationsWithPastDue')}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Switch
              disabled={!isEditable}
              label='Limit athletes to one class reservation per day'
              checked={form.values.classLimitOneReservationPerDay}
              data-cy='gymSettingsLimitOneClassReservationPerDay'
              {...form.getInputProps('classLimitOneReservationPerDay')}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Switch
              disabled={!isEditable}
              label='Allow late cancellations'
              checked={form.values.classAllowLateCancellation}
              {...form.getInputProps('classAllowLateCancellation')}
            />
          </Grid.Col>

          <Grid.Col span={3}>
            <Group noWrap grow spacing='xs'>
              <NumberInput
                size='xs'
                disabled={!isEditable}
                min={0}
                max={setMaxLateCancellationIntervalCount(form.values.classCancellationWindowInterval)}
                {...form.getInputProps('classCancellationWindowIntervalCount')}
                required
                data-cy='gymSettingsReservationTimeLateCancel'
              />
              <Select
                size='xs'
                data={timeUnitOptions}
                disabled={!isEditable}
                {...form.getInputProps('classCancellationWindowInterval')}
                data-cy='gymSettingsReservationTimeLateCancelSelect'
              />
            </Group>
          </Grid.Col>

          <Grid.Col span={9}>
            <Text size='sm'>
              {form.values.classAllowLateCancellation
                ? 'Time before the start of a class when a cancellation is considered late'
                : 'Time before the start of a class when cancellations are no longer allowed'}
            </Text>
          </Grid.Col>

          {form.values.classAllowLateCancellation && (
            <>
              <Grid.Col span={12}>
                <Group noWrap spacing='md'>
                  <Switch
                    disabled={!isEditable}
                    checked={form.values.classUseCancellationFee}
                    data-cy='classUseCancellationFee'
                    label='Auto-charge fee for late cancellation'
                    {...form.getInputProps('classUseCancellationFee')}
                  />
                  {form.values.classUseCancellationFee && (
                    <MoneyField
                      country={gymData?.address.country}
                      size='xs'
                      disabled={!isEditable || !form.values.classUseCancellationFee}
                      min={0}
                      {...form.getInputProps('classCancellationFee')}
                      data-cy='classLateCancelFee'
                    />
                  )}
                </Group>
              </Grid.Col>

              <Grid.Col span={12}>
                <Switch
                  disabled={!isEditable}
                  label='Deduct credit for late cancellation'
                  checked={form.values.classCancellationCreditDeduction}
                  {...form.getInputProps('classCancellationCreditDeduction')}
                  data-cy='classLateCancelCreditDeduction'
                />
              </Grid.Col>
            </>
          )}

          <Grid.Col span={12}>
            <Divider mx='-sm' my='sm' />
            <Group noWrap spacing='md'>
              <Switch
                disabled={!isEditable}
                checked={form.values.classUseNoShowFee}
                data-cy='classUseNoShowFee'
                label='Auto-charge fee for no show'
                {...form.getInputProps('classUseNoShowFee')}
              />
              {form.values.classUseNoShowFee && (
                <MoneyField
                  country={gymData?.address.country}
                  size='xs'
                  disabled={!isEditable || !form.values.classUseNoShowFee}
                  min={0}
                  {...form.getInputProps('classNoShowFee')}
                  data-cy='classNoShowFee'
                />
              )}
            </Group>
          </Grid.Col>

          <Grid.Col span={12}>
            <Switch
              disabled={!isEditable}
              label='Deduct credit for no show'
              checked={form.values.classNoShowCreditDeduction}
              {...form.getInputProps('classNoShowCreditDeduction')}
              data-cy='classNoShowCreditDeduction'
            />
          </Grid.Col>
        </Grid>

        <Divider mx='-sm' my='md' />

        <Title order={4} px='xs'>
          Appointments
        </Title>

        <Grid align='center' gutter='md' p='xs'>
          <Grid.Col span={12}>
            <Switch
              my='xs'
              disabled={!isEditable}
              label='Limit athletes to one appointment per day'
              checked={form.values.appointmentLimitOnePerDay}
              data-cy='gymSettingsLimitOneAppointmentPerDay'
              {...form.getInputProps('appointmentLimitOnePerDay')}
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Switch
              my='xs'
              disabled={!isEditable}
              label='Allow late cancellations'
              checked={form.values.appointmentAllowLateCancellation}
              data-cy='appAllowLateCancellation'
              {...form.getInputProps('appointmentAllowLateCancellation')}
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <Group noWrap grow spacing='xs'>
              <NumberInput
                size='xs'
                disabled={!isEditable}
                min={0}
                max={setMaxLateCancellationIntervalCount(form.values.appointmentCancellationWindowInterval)}
                {...form.getInputProps('appointmentCancellationWindowIntervalCount')}
                required
                data-cy='gymSettingsAppointmentLateCancel'
              />
              <Select
                size='xs'
                data={timeUnitOptions}
                disabled={!isEditable}
                {...form.getInputProps('appointmentCancellationWindowInterval')}
                data-cy='gymSettingsAppointmentLateCancelSelect'
              />
            </Group>
          </Grid.Col>
          <Grid.Col span={9}>
            <Text size='sm'>
              {form.values.appointmentAllowLateCancellation
                ? 'Time before the start of an appointment when a cancellation is considered late'
                : 'Time before the start of an appointment when cancellations are no longer allowed'}
            </Text>
          </Grid.Col>
          {form.values.appointmentAllowLateCancellation && (
            <>
              <Grid.Col span={12}>
                <Group noWrap spacing='md'>
                  <Switch
                    disabled={!isEditable}
                    checked={form.values.appointmentUseCancellationFee}
                    data-cy='appUseCancellationFee'
                    label='Auto-charge fee for late cancellation'
                    {...form.getInputProps('appointmentUseCancellationFee')}
                  />
                  {form.values.appointmentUseCancellationFee && (
                    <MoneyField
                      country={gymData?.address.country}
                      size='xs'
                      disabled={!isEditable || !form.values.appointmentUseCancellationFee}
                      min={0}
                      {...form.getInputProps('appointmentCancellationFee')}
                      data-cy='appLateCancelFee'
                    />
                  )}
                </Group>
              </Grid.Col>

              <Grid.Col span={12}>
                <Switch
                  disabled={!isEditable}
                  label='Deduct credit for late cancellation'
                  checked={form.values.appointmentCancellationCreditDeduction}
                  {...form.getInputProps('appointmentCancellationCreditDeduction')}
                  data-cy='appLateCancelCreditDeduction'
                />
              </Grid.Col>
            </>
          )}
          <Grid.Col span={12}>
            <Divider mx='-sm' my='sm' />
            <Group noWrap spacing='md'>
              <Switch
                disabled={!isEditable}
                checked={form.values.appointmentUseNoShowFee}
                data-cy='appUseNoShowFee'
                label='Auto-charge fee for no show'
                {...form.getInputProps('appointmentUseNoShowFee')}
              />
              {form.values.appointmentUseNoShowFee && (
                <MoneyField
                  country={gymData?.address.country}
                  size='xs'
                  disabled={!isEditable || !form.values.appointmentUseNoShowFee}
                  min={0}
                  {...form.getInputProps('appointmentNoShowFee')}
                  data-cy='appNoShowFee'
                />
              )}
            </Group>
          </Grid.Col>

          <Grid.Col span={12}>
            <Switch
              disabled={!isEditable}
              label='Deduct credit for no show'
              checked={form.values.appointmentNoShowCreditDeduction}
              {...form.getInputProps('appointmentNoShowCreditDeduction')}
              data-cy='appNoShowCreditDeduction'
            />
          </Grid.Col>
        </Grid>
        <Divider mx='-sm' my='md' />
        <Title order={4} px='xs'>
          Cancellation Tax Rate
        </Title>
        <Grid align='center' gutter={6} p='xs'>
          <Grid.Col span='content'>
            <NumberInput
              disabled={!isEditable}
              min={0}
              max={100}
              precision={3}
              {...form.getInputProps('lateCancelNoShowTaxRate')}
            />
          </Grid.Col>
          <Grid.Col span='content'>
            <Text size='sm'>% Applied to Late Cancel and No Show fees</Text>
          </Grid.Col>
        </Grid>
      </form>
    </Paper>
  );
};

export default ReservationWaitlistCancelationsCard;
